/**
 * 封装 fetch 请求，自动注入 Authorization header，处理 401 跳转
 */
import { authStore } from "@/store/auth";
import { message } from "ant-design-vue";
import router from "@/router";
import { API_BASE_URL } from "@/utils/api";

const BASE_URL = API_BASE_URL;

/**
 * 通用请求函数
 * @param {string} url — 相对路径（如 "/v1/analytics/auth/login"）或绝对 URL
 * @param {RequestInit} options — fetch 选项
 * @returns {Promise<object>} — 解析后的 JSON 响应
 */
export async function request(url, options = {}) {
  const fullUrl = url.startsWith("http") ? url : BASE_URL + url;

  // 注入 Authorization header
  const headers = { ...options.headers };
  if (authStore.state.token) {
    headers["Authorization"] = "Bearer " + authStore.state.token;
  }

  const res = await fetch(fullUrl, { ...options, headers });
  const json = await res.json();

  // 401: token 过期或未登录
  if (json.code === 401) {
    authStore.clearAuth();
    message.warning("登录已过期，请重新登录");
    router.push("/login");
    return Promise.reject(json);
  }

  return json;
}

/**
 * GET 请求
 * @param {string} url
 * @param {object} params — 查询参数对象
 */
export function get(url, params = {}) {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") query.set(k, v);
  }
  const qs = query.toString();
  return request(qs ? `${url}?${qs}` : url);
}

/**
 * POST 请求（application/x-www-form-urlencoded）
 * @param {string} url
 * @param {object} data — 请求体参数对象
 */
export function post(url, data = {}) {
  const body = new URLSearchParams();
  for (const [k, v] of Object.entries(data)) {
    if (v != null) body.set(k, String(v));
  }
  return request(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
    body: body.toString(),
  });
}

/**
 * POST 请求（application/json）
 * @param {string} url
 * @param {object} data
 */
export function postJson(url, data = {}) {
  return request(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

/**
 * POST 请求（multipart/form-data，用于文件上传）
 * @param {string} url
 * @param {FormData} formData
 */
export function postForm(url, formData) {
  return request(url, {
    method: "POST",
    body: formData,
  });
}
